import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  PointerSensor,
  rectIntersection,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { generalActions } from "../../store/actions";
import kanbanService from "../../store/services/kanban.service";
import CreateEditTask from "../../views/scopeOverview/CreateEditTask";
import Modal from "../controls/modal/Modal";
import styles from "./Kanban.module.scss";
import KanbanCard from "./KanbanCard";
import KanbanLane from "./KanbanLane";
import {
  ESTIMATED_CARD_HEIGHT,
  KANBAN_ACTIONS,
  KANBAN_STATUS_LABEL,
  LANE_ORDER
} from "./kanban.constants";
import { getEditPermission } from "../../helpers/utils";

const Kanban = (props) => {
  const {
    parent,
    parentId,
    searchTerm = '',
    heightDelta = "0px",
    className = '',
    laneClassName = '',
    laneCardClassName = '',
    onCreateTask,
    setOnCreateTask
  } = props;

  // ─── Lane state: one entry per status ────────────────────────────
  const [lanes, setLanes] = useState(() => {
    const initial = {};
    LANE_ORDER.forEach((status) => {
      initial[status] = {
        items: [],
        totalCount: 0,
        hasMore: false,
        page: 1,
        isLoading: false,
      };
    });
    return initial;
  });

  const [collapsedLanes, setCollapsedLanes] = useState({});
  const [activeCard, setActiveCard] = useState(null);
  const [pageSize, setPageSize] = useState(null);
  const [initialLoading, setInitialLoading] = useState(false);

  // ─── Edit modal state ────────────────────────────────────────────
  const intialModalState = {
    showModal: false,
    showDeleteModal: false,
    taskId: null,
    payload: null,
  };
  const [modalState, setModalState] = useState(intialModalState);

  const [isButtonLoading, setIsButtonLoading] = useState(false);

  // ─── Refs ────────────────────────────────────────────────────────
  const containerRef = useRef(null);
  const rollbackRef = useRef(null);

  // ─── DnD sensors ─────────────────────────────────────────────────
  const mouseSensor = useSensor(MouseSensor, { activationConstraint: { distance: 5 } });
  const touchSensor = useSensor(TouchSensor, { activationConstraint: { distance: 5 } });
  const keyboardSensor = useSensor(KeyboardSensor, { activationConstraint: { distance: 5 } });
  const pointerSensor = useSensor(PointerSensor, { activationConstraint: { distance: 5 } });
  const sensors = useSensors(mouseSensor, touchSensor, keyboardSensor, pointerSensor);

  const dispatch = useDispatch();

  useEffect(() => {
    if (onCreateTask === true) {
      handleAction({ actionType: KANBAN_ACTIONS.CREATE });
    }
  }, [onCreateTask]);
  // ─── Calculate dynamic page size based on container height ───────
  useEffect(() => {
    if (containerRef.current) {
      const containerHeight = containerRef.current.clientHeight;
      const calculated = Math.max(5, Math.ceil(containerHeight / ESTIMATED_CARD_HEIGHT));
      setPageSize(calculated);
    }
  }, []);

  // ─── Initial load when parent/parentId/pageSize changes ─────────
  useEffect(() => {
    if (!pageSize || pageSize <= 0) return;

    fetchInitialData();
  }, [parent, parentId, pageSize, searchTerm]);

  const fetchInitialData = async (noLoadingIndicator) => {
    setOnCreateTask(false);
    !noLoadingIndicator && setInitialLoading(true);
    try {
      const response = await kanbanService.getKanbanTasks({
        parent,
        parentId,
        pageNumber: 1,
        pageSize,
        searchTerm,
      });

      const newLanes = {};
      LANE_ORDER.forEach((statusCode) => {
        const laneData = response?.find((d) => d.status === statusCode);
        newLanes[statusCode] = {
          items: laneData?.tasks || [],
          totalCount: laneData?.totalCount || 0,
          hasMore: laneData?.hasMore || false,
          page: 1,
          isLoading: false,
        };
      });
      setLanes(newLanes);
    } catch (error) {
      dispatch(
        generalActions.showToast({
          title: "Something went wrong. Please try again later.",
          type: "error",
        })
      );
    } finally {
      !noLoadingIndicator && setInitialLoading(false);
    }
  };

  // ─── Per-lane infinite scroll fetch ──────────────────────────────
  const fetchLanePage = useCallback(
    async (statusCode) => {
      setLanes((prev) => {
        const lane = prev[statusCode];
        if (!lane || lane.isLoading || !lane.hasMore) return prev;

        return {
          ...prev,
          [statusCode]: { ...lane, isLoading: true },
        };
      });

      try {
        // Get current page from the latest state
        const currentLane = lanes[statusCode];
        if (!currentLane || !currentLane.hasMore) return;

        const nextPage = currentLane.page + 1;
        const response = await kanbanService.getKanbanTasks({
          parent,
          parentId,
          pageNumber: nextPage,
          pageSize,
          status: statusCode,
          searchTerm,
        });

        // For per-lane fetch, response has a single lane entry
        const laneData = response?.[0];
        if (!laneData) return;

        setLanes((prev) => ({
          ...prev,
          [statusCode]: {
            items: [...prev[statusCode].items, ...(laneData.tasks || [])],
            totalCount: laneData.totalCount || prev[statusCode].totalCount,
            hasMore: laneData.hasMore || false,
            page: nextPage,
            isLoading: false,
          },
        }));
      } catch (error) {
        dispatch(
          generalActions.showToast({
            title: "Something went wrong. Please try again later.",
            type: "error",
          })
        );
        setLanes((prev) => ({
          ...prev,
          [statusCode]: { ...prev[statusCode], isLoading: false },
        }));
      }
    },
    [lanes, parent, parentId, pageSize]
  );

  // ─── Lane scroll handler ────────────────────────────────────────
  const handleLaneScroll = useCallback(
    (statusCode) => (e) => {
      const el = e.target;
      const threshold = 100;
      const lane = lanes[statusCode];
      if (!lane || lane.isLoading || !lane.hasMore) return;

      if (el.scrollHeight - el.scrollTop - el.clientHeight < threshold) {
        fetchLanePage(statusCode);
      }
    },
    [lanes, fetchLanePage]
  );

  // ─── Status helpers ──────────────────────────────────────────────
  const getStatusCode = (label) => {
    const entry = Object.entries(KANBAN_STATUS_LABEL).find(
      ([, value]) => value === label
    );
    return entry ? Number(entry[0]) : 0;
  };

  // ─── Optimistic update helpers ───────────────────────────────────
  const rollbackLanes = () => {
    if (rollbackRef.current) {
      setLanes(rollbackRef.current);
      rollbackRef.current = null;
    }
  };

  const applyOptimisticMove = (taskId, newStatusCode) => {
    rollbackRef.current = JSON.parse(JSON.stringify(lanes));
    setLanes((prev) => {
      const next = {};
      LANE_ORDER.forEach((s) => { next[s] = { ...prev[s], items: [...prev[s].items] }; });
      let movedCard = null;
      for (const statusCode of LANE_ORDER) {
        const idx = next[statusCode].items.findIndex((item) => item.id === taskId);
        if (idx !== -1) {
          movedCard = { ...next[statusCode].items[idx], status: newStatusCode };
          next[statusCode] = {
            ...next[statusCode],
            items: next[statusCode].items.filter((_, i) => i !== idx),
            totalCount: Math.max(0, next[statusCode].totalCount - 1),
          };
          break;
        }
      }
      if (movedCard) {
        next[newStatusCode] = {
          ...next[newStatusCode],
          items: [movedCard, ...next[newStatusCode].items],
          totalCount: next[newStatusCode].totalCount + 1,
        };
      }
      return next;
    });
  };

  const applyOptimisticDelete = (taskId) => {
    rollbackRef.current = JSON.parse(JSON.stringify(lanes));
    setLanes((prev) => {
      const next = {};
      LANE_ORDER.forEach((statusCode) => {
        const lane = prev[statusCode];
        const hasTask = lane.items.some((item) => item.id === taskId);
        next[statusCode] = hasTask
          ? { ...lane, items: lane.items.filter((item) => item.id !== taskId), totalCount: Math.max(0, lane.totalCount - 1) }
          : lane;
      });
      return next;
    });
  };

  // ─── Drag & Drop handlers ───────────────────────────────────────
  const handleDragStart = (e) => {
    setActiveCard(e.active.data.current);
  };

  const handleDragCancel = () => {
    setActiveCard(null);
  };

  const handleDragEnd = async (e) => {
    setActiveCard(null);
    const targetLane = e.over?.id;
    if (!targetLane) return;

    const card = e.active.data.current?.taskDetails;
    if (!card) return;

    const newStatusCode = getStatusCode(targetLane);
    if (card.status === newStatusCode) return;

    applyOptimisticMove(card.id, newStatusCode);

    try {
      await kanbanService.updateTaskStatus({
        id: card.id,
        payload: { status: newStatusCode },
      });
      rollbackRef.current = null;
      fetchInitialData(true);
    } catch (error) {
      rollbackLanes();
      dispatch(
        generalActions.showToast({
          title: "Failed to update task status. Please try again later.",
          type: "error",
        })
      );
    }
  };

  // ─── Move via card menu ──────────────────────────────────────────
  const handleMove = async (taskId, statusLabel) => {
    if (!taskId) return;
    const newStatusCode = getStatusCode(statusLabel);

    applyOptimisticMove(taskId, newStatusCode);

    try {
      await kanbanService.updateTaskStatus({
        id: taskId,
        payload: { status: newStatusCode },
      });
      rollbackRef.current = null;
      fetchInitialData(true);
    } catch (error) {
      rollbackLanes();
      dispatch(
        generalActions.showToast({
          title: "Failed to move task. Please try again later.",
          type: "error",
        })
      );
    } finally {
      resetModalState();
    }
  };

  // ─── Collapse ────────────────────────────────────────────────────
  const toggleLaneCollapse = (laneTitle) => {
    setCollapsedLanes((prev) => ({
      ...prev,
      [laneTitle]: !prev[laneTitle],
    }));
  };

  const resetModalState = () => {
    setModalState(intialModalState);
    setOnCreateTask(false);
  };

  const handleDelete = async () => {
    if (!modalState.taskId) return;
    setIsButtonLoading(true);

    const taskId = modalState.taskId;
    applyOptimisticDelete(taskId);

    try {
      await kanbanService.deleteTask({ id: taskId });
      rollbackRef.current = null;
      fetchInitialData(true);
    } catch (error) {
      rollbackLanes();
      dispatch(
        generalActions.showToast({
          title: "Failed to delete task. Please try again later.",
          type: "error",
        })
      );
    } finally {
      resetModalState();
      setIsButtonLoading(false);
    }
  };

  const handleDuplicate = async (taskId) => {
    if (!taskId) return;
    try {
      await kanbanService.duplicateTask({ id: taskId });
      fetchInitialData(true);
    } catch (error) {
      dispatch(
        generalActions.showToast({
          title: "Failed to duplicate task. Please try again later.",
          type: "error",
        })
      );
    }
    finally {
      resetModalState();
    }
  };

  const handleAction = (action) => {

    let modalState = {
      taskId: action.payload?.id,
    };

    switch (action.actionType) {
      case KANBAN_ACTIONS.CREATE:
        setModalState({
          showModal: true,
          showDeleteModal: false,
        });
        break;
      case KANBAN_ACTIONS.EDIT:
        setModalState({
          ...modalState,
          showModal: true,
          showDeleteModal: false,
        });
        break;
      case KANBAN_ACTIONS.DELETE:
        setModalState({
          ...modalState,
          showModal: false,
          showDeleteModal: true,
          payload: action.payload,
        });
        break;
      case KANBAN_ACTIONS.DUPLICATE:
        setModalState({
          ...modalState,
          showModal: false,
          showDeleteModal: false,
        });
        handleDuplicate(action.payload?.id);
        break;
      case KANBAN_ACTIONS.MOVE:
        setModalState({
          ...modalState,
          showModal: false,
          showDeleteModal: false,
          payload: action.payload,
        });
        handleMove(action.payload?.id, action.payload?.newStatus);
        break;
      default:
        break;
    }
  };

  // ─── Render helper: get lane items by label ──────────────────────
  const getLaneItems = (statusCode) => lanes[statusCode]?.items || [];
  const getLaneTotalCount = (statusCode) => lanes[statusCode]?.totalCount || 0;

  return (
    <>
      <DndContext
        collisionDetection={rectIntersection}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
        sensors={sensors}
      >
        <div
          ref={containerRef}
          className={`${styles.KanbanContainer} ${className}`}
          style={{ height: `calc(100vh - ${heightDelta})` }}
        >
          {initialLoading ? (
            <div className={styles.laneLoader}>
              <div className={styles.laneLoaderSpinner} />
            </div>
          ) : (
            <div className={`row gx-2 h-100 ${styles.KanbanLanesContainer}`}>
              {LANE_ORDER.map((statusCode) => {
                const laneLabel = KANBAN_STATUS_LABEL[statusCode];
                const lane = lanes[statusCode];
                const isCollapsed = collapsedLanes[laneLabel];

                return (
                  <div
                    key={statusCode}
                    className={`col ${styles.KanbanLane} ${isCollapsed ? styles.KanbanLaneCollapsed : ""
                      } ${laneClassName}`}
                  >
                    <KanbanLane
                      title={laneLabel}
                      CardClassName={laneCardClassName}
                      items={getLaneItems(statusCode)}
                      handleAction={handleAction}
                      heightDelta={heightDelta}
                      isCollapsed={isCollapsed}
                      onToggleCollapse={toggleLaneCollapse}
                      totalCount={getLaneTotalCount(statusCode)}
                      onScroll={handleLaneScroll(statusCode)}
                      isLoadingMore={lane?.isLoading}
                      style={{ minHeight: '100%' }}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <DragOverlay dropAnimation={null}>
          {activeCard ? (
            <KanbanCard
              taskDetails={activeCard.taskDetails}
              className={laneCardClassName}
              index={activeCard.index}
              isOverlay
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Edit / Create Modal */}
      {modalState.showModal && (
        <CreateEditTask
          isOpen={modalState.showModal}
          isEdit={modalState.taskId}
          taskId={modalState.taskId}
          onClose={resetModalState}
          onSave={fetchInitialData}
          isViewOnly={modalState.taskId && !getEditPermission("Tasks")}
        />
      )}
      {/* Delete Modal */}
      {modalState.showDeleteModal && (
        <Modal
          title="Delete Task"
          body={
            <p className="text-sm-400 subHeading-color">
              Are you sure you want to delete the selected task - {modalState.payload?.name}?
            </p>
          }
          handleCancel={resetModalState}
          handleSave={handleDelete}
          saveBtnLabel="Delete"
          cancelBtnLabel="Cancel"
          isLoading={isButtonLoading}
        />
      )}
    </>
  );
};

export default Kanban;
